import React from 'react';
import { Button, Drawer, Tabs, Spin, Tag, Tooltip } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CANCELED, REJECTED, APPROVED, PENDING } from '../../../constants/ReceiverConstants';
import { ExtendedResources, StandardResource } from '../../../../types/resources';
import { PriceSheetType } from '../../../../types/price_sheet';
import {
  Receiver,
  ReceiverDetails,
  ReceiverRequest,
  SKUListParams,
  SKUItem,
  SKUPaginationInfo,
  DashboardData,
} from '../../../../types/receiver';
import { ScorecardData, JobStatus } from '../../../../types/scorecard';
import { PartNumberSearchParams, PartNumberPaginationInfo } from './scorecard/PartNumberBreakdown';
import { BrandCustomField, CustomFieldIdName } from '../../../../types/custom_fields';
import ReceiverProfile from './ReceiverProfile';
import ReceiverRequestPopover from './ReceiverRequestPopover';
import ReceiverRejectPopover from './ReceiverRejectPopover';
import AdoptionTab from './AdoptionTab';
import ScorecardCompletenessTab from './ScorecardCompletenessTab';
import CustomAppsEmbedded from '../settings/customApps/CustomAppsEmbedded';
import { ApplicationState } from '../../../reducers';
import { hasPermission } from '../../../utils/Permissions';

type ReceiverRequestParams = {
  priceSheetIds: number[];
  priceTypeIds: number[];
  withoutPrices: boolean;
  filterIds: number[];
  withoutInterchanges: boolean;
  interchangeQualityIds: number[];
  withoutCustomFields: boolean;
  customFieldIds: number[];
};

type ReceiverStatsDrawerProps = {
  visible: boolean;
  receiver: Receiver | null;
  receiverDetails?: ReceiverDetails;
  fetchingReceiverDetails: boolean;
  status: {
    approved?: boolean;
    pending?: boolean;
    rejected?: boolean;
    authorizedByReceiver?: boolean;
  };
  onClose: () => void;
  requestReceiverAccess: (params: {
    receiverId: number;
    brandIds: number[];
    integrationTypeId: number | null;
    selectedPriceSheetIds: number[];
    selectedPriceTypeIds: number[];
    withoutPrices: boolean;
    filterIds: number[];
    withoutInterchanges: boolean;
    interchangeQualityIds: number[];
    withoutCustomFields: boolean;
    customFieldIds: number[];
    isNewRequest: boolean;
  }) => void;
  approveReceivers: (params: {
    receiverId: number;
    brandIds: number[];
    selectedPriceSheetIds: number[];
    selectedPriceTypeIds: number[];
    withoutPrices: boolean;
    filterIds: number[];
    withoutInterchanges: boolean;
    interchangeQualityIds: number[];
    withoutCustomFields: boolean;
    customFieldIds: number[];
  }) => void;
  declineReceivers: (receiverId: number, brandId: number[], reason?: string) => void;
  cancelRequests: (receiverId: number, brandIds: number[]) => void;
  priceSheets: PriceSheetType[];
  priceTypes: ExtendedResources[];
  interchangeQuality: StandardResource[];
  customFields: BrandCustomField[];
  fetchingCustomFields: boolean;
  customFieldsIdNameList: CustomFieldIdName[];
  adoptionData?: DashboardData;
  adoptionLoading?: boolean;
  onRefreshAdoption?: () => void;
  onFetchAdoptionStats?: (receiverId: number) => Promise<any>;
  onFetchSkus?: (receiverId: number, params: SKUListParams) => Promise<any>;
  skuData?: { [receiverId: number]: SKUItem[] };
  skuPagination?: { [receiverId: number]: SKUPaginationInfo };
  skuLoading?: { [receiverId: number]: boolean };
  skuSearchError?: { [receiverId: number]: string | null };
  scorecardData?: { [receiverId: number]: ScorecardData };
  scorecardLoading?: { [receiverId: number]: boolean };
  onRefreshScorecard?: () => void;
  onFetchScorecardData?: (receiverId: number) => Promise<any>;
  onSearchPartNumbers?: (receiverId: number, params: PartNumberSearchParams) => Promise<any>;
  partNumberPagination?: PartNumberPaginationInfo;
  partNumberLoading?: boolean;
  refreshJobStatus?: JobStatus | null;
  onCheckJobStatus?: (jobId: string) => void;
  adoptionRefreshJobStatus?: JobStatus | null;
  onCheckAdoptionJobStatus?: (jobId: string) => void;
};

const ReceiverStatsDrawer: React.FC<ReceiverStatsDrawerProps> = ({
  visible,
  receiver,
  receiverDetails,
  fetchingReceiverDetails,
  status,
  onClose,
  requestReceiverAccess,
  approveReceivers,
  declineReceivers,
  cancelRequests,
  priceSheets,
  priceTypes,
  interchangeQuality,
  customFields,
  fetchingCustomFields,
  customFieldsIdNameList,
  adoptionData,
  adoptionLoading,
  onRefreshAdoption,
  onFetchAdoptionStats,
  onFetchSkus,
  skuData,
  skuPagination,
  skuLoading,
  skuSearchError,
  scorecardData,
  scorecardLoading,
  onRefreshScorecard,
  onFetchScorecardData,
  onSearchPartNumbers,
  partNumberPagination,
  partNumberLoading,
  refreshJobStatus,
  onCheckJobStatus,
  adoptionRefreshJobStatus,
  onCheckAdoptionJobStatus,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<string>('brands');

  const user = useSelector((state: ApplicationState) => state.user.user);
  const integrations = useSelector((state: ApplicationState) => state.tools.replit.integrations);
  const customAppConfigs = useSelector(
    (state: ApplicationState) => state.customAppsSettings.configs
  );

  const canAccessCustomApps = React.useMemo(() => hasPermission(user, 'can_access_replit'), [user]);

  // Get receiver-level custom apps
  const receiverCustomApps = React.useMemo(() => {
    if (
      !canAccessCustomApps ||
      integrations.length === 0 ||
      customAppConfigs.length === 0 ||
      !receiver
    ) {
      return [];
    }

    const receiverId = receiver.receiver_id;
    const enabledConfigs = customAppConfigs.filter(
      config =>
        config.level === 'RECEIVER' &&
        config.is_enabled &&
        (config.entity_id === receiverId || !config.entity_id)
    );

    if (enabledConfigs.length === 0) return [];

    const enabledAppIds = enabledConfigs.map(config => config.app_id);
    const filteredApps = integrations.filter(integration => enabledAppIds.includes(integration.id));

    return filteredApps
      .map(app => {
        const config = enabledConfigs.find(c => c.app_id === app.id);
        return {
          ...app,
          displayOrder: config?.display_order || 999,
          displayName: config?.display_name || app.app_id,
        };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [canAccessCustomApps, integrations, customAppConfigs, receiver]);

  // State for popover visibility
  const [editBrandReceiver, setEditBrandReceiver] = React.useState<string | null>(null);
  const [approveBrandReceiver, setApproveBrandReceiver] = React.useState<string | null>(null);
  const [requestBrandReceiver, setRequestBrandReceiver] = React.useState<string | null>(null);

  const getReceiverStatus = (request: ReceiverRequest) => {
    return request.status || '';
  };

  const handleApproveReceivers = (
    receiverId: number,
    brandIds: number[],
    values: ReceiverRequestParams
  ) => {
    const {
      priceSheetIds,
      priceTypeIds,
      withoutPrices,
      filterIds,
      withoutInterchanges,
      interchangeQualityIds,
      withoutCustomFields,
      customFieldIds,
    } = values;
    return approveReceivers({
      receiverId,
      brandIds,
      selectedPriceSheetIds: priceSheetIds,
      selectedPriceTypeIds: priceTypeIds,
      withoutPrices,
      filterIds,
      withoutInterchanges,
      interchangeQualityIds,
      withoutCustomFields,
      customFieldIds,
    });
  };

  const handleRequestReceivers = (
    receiverId: number,
    integrationTypeId: number | null,
    brandIds: number[],
    values: ReceiverRequestParams,
    isNewRequest: boolean
  ) => {
    return requestReceiverAccess({
      receiverId,
      integrationTypeId,
      brandIds,
      selectedPriceSheetIds: values.priceSheetIds,
      selectedPriceTypeIds: values.priceTypeIds,
      withoutPrices: values.withoutPrices,
      filterIds: values.filterIds,
      withoutInterchanges: values.withoutInterchanges,
      interchangeQualityIds: values.interchangeQualityIds,
      withoutCustomFields: values.withoutCustomFields,
      customFieldIds: values.customFieldIds,
      isNewRequest,
    });
  };

  const createActionButton = (request: ReceiverRequest) => {
    const status = getReceiverStatus(request);
    const brandId = request.brand_id;
    const receiverId = request.receiverId;
    const integrationTypeId = receiverId ? null : request.integrationTypeId;

    return (
      <div className="channel-table-header__button-group">
        {((status === PENDING && request.authorized_by_brand_at) || status === APPROVED) && (
          <ReceiverRequestPopover
            brandId={brandId}
            visible={`${request.brand_id}${request.receiverId}` === editBrandReceiver}
            buttonName={t('receiver:actions.edit')}
            title={t('receiver:edit_title')}
            selectedFilterIds={request.filter_ids}
            priceSheets={priceSheets}
            priceTypes={priceTypes}
            interchangeQuality={interchangeQuality}
            defaultSelectedPriceSheetIds={request.price_sheet_ids || []}
            defaultSelectedPriceTypeIds={request.price_type_ids || []}
            defaultWithoutPrices={request.without_prices === 1}
            defaultWithoutInterchanges={request.without_interchanges === 1}
            defaultSelectedInterchangeQltyIds={request.with_interchange_type_records}
            defaultWithoutCustomfields={request.without_custom_fields === 1}
            defaultSelectedCustomFields={request.custom_field_ids}
            customFields={customFields}
            fetchingCustomFields={fetchingCustomFields}
            customFieldsIdNameList={customFieldsIdNameList}
            hide={() => setEditBrandReceiver(null)}
            open={() => {
              setEditBrandReceiver(`${brandId}${receiverId}`);
            }}
            requestReceiver={async (values: ReceiverRequestParams) => {
              const isNewRequest =
                !request.price_sheet_ids &&
                !request.price_type_ids &&
                request.without_prices === 0 &&
                request.without_interchanges === 0 &&
                !request.with_interchange_type_records &&
                !request.custom_field_ids &&
                request.without_custom_fields === 0;

              const response = await handleRequestReceivers(
                receiverId,
                request.integrationTypeId,
                [brandId],
                values,
                isNewRequest
              );
              setEditBrandReceiver(null);
              return response;
            }}
          />
        )}
        {((status === PENDING && request.authorized_by_receiver_at) || status === APPROVED) && (
          <ReceiverRejectPopover
            receiverName={request.name}
            handleReject={(reason?: string) =>
              declineReceivers(request.receiverId, [request.brand_id], reason)
            }
          >
            <Button type="primary" size="small" ghost>
              {t('receiver:reject')}
            </Button>
          </ReceiverRejectPopover>
        )}
        {((status === PENDING && request.authorized_by_receiver_at) ||
          (status === REJECTED && !request.declined_by_receiver_at)) && (
          <ReceiverRequestPopover
            brandId={brandId}
            visible={`${brandId}${receiverId}` === approveBrandReceiver}
            buttonName={t('receiver:approve')}
            title={t('receiver:approve_title')}
            selectedFilterIds={request.filter_ids}
            priceSheets={priceSheets}
            priceTypes={priceTypes}
            interchangeQuality={interchangeQuality}
            defaultWithoutCustomfields={request.without_custom_fields === 1}
            defaultSelectedCustomFields={request.custom_field_ids}
            customFields={customFields}
            fetchingCustomFields={fetchingCustomFields}
            customFieldsIdNameList={customFieldsIdNameList}
            hide={() => setApproveBrandReceiver(null)}
            open={() => {
              setApproveBrandReceiver(`${brandId}${receiverId}`);
            }}
            requestReceiver={async (values: ReceiverRequestParams) => {
              const response = await handleApproveReceivers(receiverId, [brandId], values);
              setApproveBrandReceiver(null);
              return response;
            }}
          />
        )}
        {status === PENDING && request.authorized_by_brand_at && (
          <Button
            onClick={() => cancelRequests(receiverId, [brandId])}
            type="primary"
            ghost
            size="small"
          >
            {t('common:cancel')}
          </Button>
        )}
        {(status === '' || status === CANCELED) && (
          <ReceiverRequestPopover
            brandId={brandId}
            visible={`${brandId}${receiverId}` === requestBrandReceiver}
            buttonName={t('receiver:offer_product_data')}
            title={t('receiver:offer_product_data')}
            selectedFilterIds={request.filter_ids}
            priceSheets={priceSheets}
            priceTypes={priceTypes}
            interchangeQuality={interchangeQuality}
            customFields={customFields}
            fetchingCustomFields={fetchingCustomFields}
            customFieldsIdNameList={customFieldsIdNameList}
            defaultWithoutCustomfields={request.without_custom_fields === 1}
            defaultSelectedCustomFields={request.custom_field_ids}
            hide={() => setRequestBrandReceiver(null)}
            open={() => {
              setRequestBrandReceiver(`${brandId}${receiverId}`);
            }}
            requestReceiver={async (values: ReceiverRequestParams) => {
              const response = await handleRequestReceivers(
                receiverId,
                integrationTypeId,
                [brandId],
                values,
                true
              );
              setRequestBrandReceiver(null);
              return response;
            }}
          />
        )}
      </div>
    );
  };

  const createStatus = (request: ReceiverRequest) => {
    const status = getReceiverStatus(request);
    if (status === REJECTED)
      return (
        <span>
          <Tag color="red">
            {t('receiver:status.rejected')}
            {request.declined_by_brand_reason && (
              <Tooltip title={request.declined_by_brand_reason}>
                <FileSearchOutlined className="receiver__declined-icon" />
              </Tooltip>
            )}
          </Tag>
        </span>
      );
    if (status === APPROVED) return <Tag color="green">{t('receiver:status.approved')}</Tag>;
    if (status === PENDING) return <Tag color="orange">{t('receiver:status.pending')}</Tag>;
  };

  const renderBrandsList = () => {
    if (!receiver || !receiver.requests.length) {
      return <div className="p-4 text-center text-gray-500">{t('receiver:noBrands')}</div>;
    }

    return (
      <div className="receiver-drawer__brands-list h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {receiver.requests.map((request: ReceiverRequest) => (
            <div
              key={`${request.brand_id}_${request.receiverId}`}
              className="px-4 py-3 border-b hover:bg-gray-50 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{request.brandName}</span>
                </div>
              </div>
              <div className="flex-shrink-0 w-24">{createStatus(request)}</div>
              <div className="flex-shrink-0 w-32">{createActionButton(request)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReceiverProfile = () => {
    if (fetchingReceiverDetails) {
      return (
        <div className="flex justify-center p-8">
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div className="p-4">
        <ReceiverProfile
          receiverDetails={receiverDetails}
          approved={status.approved || status.authorizedByReceiver}
        />
      </div>
    );
  };

  const renderAdoptionTab = () => {
    return (
      <AdoptionTab
        data={adoptionData}
        loading={adoptionLoading}
        onRefresh={onRefreshAdoption}
        onFetchAdoptionStats={onFetchAdoptionStats}
        receiverId={receiver?.receiver_id}
        onFetchSkus={onFetchSkus}
        skuData={skuData}
        skuPagination={skuPagination}
        skuLoading={skuLoading}
        skuSearchError={skuSearchError}
        refreshJobStatus={adoptionRefreshJobStatus}
        onCheckJobStatus={onCheckAdoptionJobStatus}
      />
    );
  };

  const renderScorecardTab = () => {
    const currentReceiverScorecardData = receiver?.receiver_id
      ? scorecardData?.[receiver.receiver_id]
      : undefined;
    const currentScorecardLoading = receiver?.receiver_id
      ? scorecardLoading?.[receiver.receiver_id] || false
      : false;

    return (
      <ScorecardCompletenessTab
        data={currentReceiverScorecardData}
        loading={currentScorecardLoading}
        onRefresh={onRefreshScorecard}
        receiverId={receiver?.receiver_id}
        onFetchScorecardData={onFetchScorecardData}
        onSearchPartNumbers={onSearchPartNumbers}
        partNumberPagination={partNumberPagination}
        partNumberLoading={partNumberLoading}
        refreshJobStatus={refreshJobStatus}
        onCheckJobStatus={onCheckJobStatus}
      />
    );
  };

  const renderCustomApp = (appId: number) => {
    return (
      <CustomAppsEmbedded
        key={`custom-app-${appId}`}
        receiverId={receiver?.receiver_id}
        appId={appId}
        hideAppSelector
      />
    );
  };

  const tabItems = [
    {
      key: 'brands',
      label: t('receiver:brands'),
      children: renderBrandsList(),
    },
    {
      key: 'profile',
      label: t('receiver:receiverProfile'),
      children: renderReceiverProfile(),
    },
    ...(receiver?.has_adoption_import_enabled
      ? [
          {
            key: 'adoption',
            label: t('receiver:adoption'),
            children: renderAdoptionTab(),
          },
        ]
      : []),
    {
      key: 'scorecard',
      label: t('receiver:scorecardCompleteness'),
      children: renderScorecardTab(),
    },
    ...receiverCustomApps.map(app => ({
      key: `custom-app-${app.id}`,
      label: app.displayName,
      children: renderCustomApp(app.id),
    })),
  ];

  if (!receiver) return null;

  return (
    <Drawer
      title={
        <div className="flex justify-between items-center">
          {receiver?.name}
          <div className="mr-4">
            <span className="text-gray-700">{`${t('receiver:status.status')}: `}</span>
            {status.approved && <Tag color="green">{t('receiver:status.approved')}</Tag>}
            {status.pending && <Tag color="orange">{t('receiver:status.pending')}</Tag>}
            {status.rejected && <Tag color="red">{t('receiver:status.rejected')}</Tag>}
          </div>
        </div>
      }
      placement="right"
      width="80%"
      open={visible}
      onClose={onClose}
      destroyOnClose
      className="receiver-stats-drawer"
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="receiver-drawer__tabs"
      />
    </Drawer>
  );
};

export default ReceiverStatsDrawer;
